import { designVariables } from "@studio3t/design-variables";
import {
  BadgeMode,
  BadgeType,
  ButtonSmallSubtleIcon,
  DateTimePickerView,
  GenericBadge,
  GenericCheckBox,
  GenericDropdownWithSearch,
  GenericDropdownWithSearchContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
  useDateTimePicker,
} from "@studio3t/generic-components";
import {
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";

// The /audit-log filter sidebar (Figma AP_SystemLogs/filter 13759-112325,
// the Audit logs variant): the card's left rail with the timeframe picker,
// the resource dropdown, and the action/status checkbox trees — plus the
// collapse tab riding the bottom-right edge. Same anatomy as the /logs
// sidebar but its own sizing (270px rail vs 196) and its own filters, so
// the chrome recipes are kept local like the other per-page trigger
// duplicates (TableFilters/TablePagination/LogsFilterSidebar).

export const SIDEBAR_WIDTH = 270;
const TAB_WIDTH = 50;
const SPACINGS = designVariables.ui.semantic.spacings;
const SPACINGM = designVariables.ui.semantic.spacingm;
/* semantic/spacingl (18px) isn't exported by design-variables yet. */
const SPACINGL = 18;

/** The five auditable resource kinds the backend prefixes actions with
 *  (pipeline.delete, service.create, …) — the design's action rows. */
export const AUDIT_ACTION_GROUPS = [
  "pipeline",
  "service",
  "schema",
  "token",
  "workspace",
] as const;

export type AuditActionGroup = (typeof AUDIT_ACTION_GROUPS)[number];

/** HTTP status classes (1xx…5xx) with their badge faces (Figma rows
 *  13759-112370…112382: 1xx/3xx info, 2xx confirm, 4xx/5xx error; each
 *  badge carries its class icon in the badge's own subtle text color). */
export const STATUS_CLASS_ROWS: ReadonlyArray<{
  statusClass: number;
  label: string;
  icon: Icon;
  type: BadgeType;
}> = [
  {
    statusClass: 1,
    label: "1xx - informational",
    icon: Icon.Info,
    type: BadgeType.Info,
  },
  {
    statusClass: 2,
    label: "2xx - success",
    icon: Icon.Confirm,
    type: BadgeType.Confirm,
  },
  {
    statusClass: 3,
    label: "3xx - redirection",
    icon: Icon.Info,
    type: BadgeType.Info,
  },
  {
    statusClass: 4,
    label: "4xx - client errors",
    icon: Icon.Error,
    type: BadgeType.Error,
  },
  {
    statusClass: 5,
    label: "5xx - server errors",
    icon: Icon.Error,
    type: BadgeType.Error,
  },
];

export const ALL_STATUS_CLASSES: ReadonlyArray<number> = STATUS_CLASS_ROWS.map(
  (r) => r.statusClass,
);

/** The badge's own Subtle text color — the design paints each badge's icon
 *  in the SAME color as its label (the /logs sidebar's badgeIconColor
 *  recipe, plus the Confirm face this page needs). */
export function badgeIconColor(theme: DefaultTheme, type: BadgeType): string {
  switch (type) {
    case BadgeType.Info:
      return theme.badgeInfoSubtleColor;
    case BadgeType.Confirm:
      return theme.badgeConfirmSubtleColor;
    case BadgeType.Warning:
      return theme.badgeWarningSubtleColor;
    case BadgeType.Error:
      return theme.badgeErrorSubtleColor;
    default:
      return theme.semanticTextBody;
  }
}

export interface Timeframe {
  since?: Date;
  until?: Date;
}

function formatBound(date: Date): string {
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// Outside click / Escape close the panel; everything renders inside
// the field root, so plain containment is enough.
function useDismissOnOutside(
  open: boolean,
  rootRef: RefObject<HTMLElement | null>,
  onDismiss: () => void,
) {
  useEffect(() => {
    if (!open) return;
    // The panel lives in a body portal, outside the field's root — a
    // press inside it is inside the field for dismissal purposes.
    const inPanel = (target: EventTarget | null) =>
      target instanceof Element && target.closest("[data-timeframe-panel]");
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (!rootRef.current?.contains(target) && !inPanel(target)) onDismiss();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    // The panel is fixed on screen while its field scrolls with the
    // sidebar — any scroll closes it rather than leaving it adrift.
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    const onScroll = (e: Event) => {
      if (!inPanel(e.target)) onDismiss();
    };
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open, rootRef, onDismiss]);
}

// The × clearing a set bound: an absolute sibling of the field button so
// nothing interactive nests inside it.
function BoundClearButton({
  placeholder,
  testId,
  onClear,
  hovered,
  onHoverChange,
}: {
  placeholder: string;
  testId: string;
  onClear: () => void;
  /** Hover lives in the field, which outlives this button — it only
   *  renders while the bound is set. */
  hovered: boolean;
  onHoverChange: (hovered: boolean) => void;
}) {
  const theme = useTheme();
  return (
    <FieldClearButton
      type="button"
      aria-label={`Clear ${placeholder.toLowerCase()}`}
      title={`Clear ${placeholder.toLowerCase()}`}
      data-testid={`${testId}-clear`}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      onClick={onClear}
    >
      {/* The glyph is an <img> with the color baked in — CSS can't
          recolor it, so hover feedback rides the icon's own state
          colors (plus the pill on the button). */}
      <IconSVG
        icon={Icon.Close}
        size={12}
        hover={hovered}
        colors={{
          default: theme.semanticTextPlaceholder,
          hover: theme.semanticTextBody,
        }}
      />
    </FieldClearButton>
  );
}

type DateTimePicker = ReturnType<typeof useDateTimePicker>;

// The calendar panel itself: the lib picker's exported view wired to the
// hook state that drives it (its stock component would put its own
// trigger input in between).
function TimeframeCalendarPanel({
  label,
  picker,
  anchor,
  onConfirm,
  onCancel,
}: {
  label: string;
  picker: DateTimePicker;
  /** Where the panel sits on screen — under the field's bottom-left. */
  anchor: { top: number; left: number };
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Portalled to the body: the sidebar's scroll viewport is its own
  // stacking context, so a panel rendered inside it could never rise
  // above the collapse tab next to that viewport. Being at the end of the
  // document puts it last in the tab order, so the dialog takes focus as
  // it opens and the next Tab lands on its first control; the field takes
  // focus back when the panel closes (TTTL-186).
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  return createPortal(
    <TimeframePopover
      ref={panelRef}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      style={anchor}
      data-timeframe-panel=""
    >
      <DateTimePickerView
        viewMode={picker.viewMode}
        displayedMonth={picker.displayedMonth}
        displayedYear={picker.displayedYear}
        draftDate={picker.draftDate}
        onToggleViewMode={picker.toggleViewMode}
        onSelectMonth={picker.selectMonth}
        onSelectDay={picker.selectDay}
        onNavigatePrevMonth={picker.navigatePrevMonth}
        onNavigateNextMonth={picker.navigateNextMonth}
        onNavigatePrevYear={picker.navigatePrevYear}
        onNavigateNextYear={picker.navigateNextYear}
        onConfirm={onConfirm}
        onCancel={onCancel}
        timeProps={{
          hours: picker.hours,
          minutes: picker.minutes,
          seconds: picker.seconds,
          milliseconds: picker.milliseconds,
          hideMilliseconds: picker.hideMilliseconds,
          onChangeHours: picker.onChangeHours,
          onChangeMinutes: picker.onChangeMinutes,
          onChangeSeconds: picker.onChangeSeconds,
          onChangeMilliseconds: picker.onChangeMilliseconds,
        }}
      />
    </TimeframePopover>,
    document.body,
  );
}

// The calendar panel's open state for one bound field: where it sits,
// how it opens, closes and confirms, and where focus goes around it.
function useTimeframePanel(picker: DateTimePicker) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState({ top: 0, left: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const {
    open: openPicker,
    cancel: cancelPicker,
    confirm: confirmPicker,
  } = picker;

  // Focus comes back to the field when the panel closes while it held
  // focus (Cancel, OK, Escape) — a click elsewhere keeps its own target.
  const restoreFocus = useCallback(() => {
    const active = document.activeElement;
    if (active?.closest("[data-timeframe-panel]")) triggerRef.current?.focus();
  }, []);

  const close = useCallback(() => {
    restoreFocus();
    cancelPicker();
    setOpen(false);
  }, [cancelPicker, restoreFocus]);

  const confirm = useCallback(() => {
    restoreFocus();
    confirmPicker();
    setOpen(false);
  }, [confirmPicker, restoreFocus]);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    // The panel is positioned on screen, not in the field (the sidebar
    // scrolls, and a panel inside its scroll region would be clipped) —
    // anchored under the field's bottom-left as it stands when opened.
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchor({
        top: rect.bottom + designVariables.primitives.spacings.m,
        left: rect.left,
      });
    }
    openPicker();
    setOpen(true);
  };

  useDismissOnOutside(open, rootRef, close);

  return { open, anchor, rootRef, triggerRef, toggle, close, confirm };
}

// One bound of the timeframe (Start date / End date): the design's
// underline input face with a decorative calendar glyph — the whole
// field is the button, the icon itself is deliberately NOT interactive.
// Clicking the field opens the calendar panel DIRECTLY. A set bound
// clears via the small × riding the field.
function TimeframeBoundField({
  placeholder,
  testId,
  value,
  onChange,
}: {
  placeholder: string;
  testId: string;
  value: Date | undefined;
  onChange: (d: Date | undefined) => void;
}) {
  const [clearHovered, setClearHovered] = useState(false);
  const picker = useDateTimePicker({
    value,
    onChange: (d) => onChange(d),
  });
  const panel = useTimeframePanel(picker);

  return (
    <TimeframeRoot ref={panel.rootRef}>
      <TimeframeInput
        ref={panel.triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={panel.open}
        data-testid={testId}
        onClick={panel.toggle}
      >
        <TimeframeText $placeholder={value === undefined}>
          {value ? formatBound(value) : placeholder}
        </TimeframeText>
        {/* Decorative — the field itself is the control. */}
        <IconSVG
          icon={Icon.Date}
          size={16}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </TimeframeInput>
      {value !== undefined && (
        <BoundClearButton
          placeholder={placeholder}
          testId={testId}
          onClear={() => {
            onChange(undefined);
            if (panel.open) panel.close();
          }}
          hovered={clearHovered}
          onHoverChange={setClearHovered}
        />
      )}
      {panel.open && (
        <TimeframeCalendarPanel
          label={placeholder}
          picker={picker}
          anchor={panel.anchor}
          onConfirm={panel.confirm}
          onCancel={panel.close}
        />
      )}
    </TimeframeRoot>
  );
}

/** The actor filter's pick for "entries the AI agent performed"
 *  (actor.source === "agent"); a leading "@" keeps it apart from any email
 *  or name the Actor column could show. */
export const AGENT_ACTOR_FILTER = "@agent";

/** The "agent" badge (Figma Badge 15528-4476): Info Subtle with the AI
 *  sparkle leading its label — the Actor column's marker and the actor
 *  dropdown's agent row share it. */
export function AgentBadge() {
  const theme = useTheme();
  return (
    <GenericBadge type={BadgeType.Info} mode={BadgeMode.Subtle}>
      <IconSVG
        icon={Icon.AI}
        size={12}
        colors={{ default: theme.statusInfo }}
      />
      agent
    </GenericBadge>
  );
}

// Rejected requests can be audited before any resource exists (e.g. a 422
// pipeline.create), leaving the entry's resource empty — such entries stay
// filterable under this label instead of a blank row.
const NO_RESOURCE_LABEL = "(no resource)";

function resourceLabel(resource: string): string {
  return resource === "" ? NO_RESOURCE_LABEL : resource;
}

// A pick rendered as something other than its text — the actor
// dropdown's agent badge row (Figma DropdownMenu 15642-229044).
interface PinnedOption {
  value: string;
  label: string;
  render: ReactElement;
}

interface SingleSelectItemsInput {
  allLabel: string;
  pinned: PinnedOption[];
  options: string[];
  optionLabel: (v: string) => string;
  selected: string;
  onChange: (v: string) => void;
}

// One "All …" row, then any pinned picks, above every distinct value seen
// in the fetched window; "all" is the sentinel the page treats as no filter.
// No closeOnSelect on the rows: the searchable dropdown closes on select
// anyway, and it forwards the flag to the row's DOM node (a React
// unknown-prop error on every open — see OpenPipelineMenu).
function buildSingleSelectItems({
  allLabel,
  pinned,
  options,
  optionLabel,
  selected,
  onChange,
}: SingleSelectItemsInput): ListItemProps[][] {
  return [
    [
      {
        id: "all",
        label: allLabel,
        type: "text" as const,
        value: "all",
        selected: { value: selected === "all", icon: "dot" as const },
        onClick: () => onChange("all"),
      },
      ...pinned.map((pin) => ({
        id: pin.value,
        label: pin.label,
        customLabelRender: pin.render,
        type: "text" as const,
        value: pin.value,
        selected: { value: pin.value === selected, icon: "dot" as const },
        onClick: () => onChange(pin.value),
      })),
      ...options.map((option) => ({
        id: option === "" ? "empty" : option,
        label: optionLabel(option),
        type: "text" as const,
        value: option,
        selected: { value: option === selected, icon: "dot" as const },
        onClick: () => onChange(option),
      })),
    ],
  ];
}

// The collapse tab (the /logs sidebar's recipe): a passive bordered
// container with the standard S subtle icon button inside — the lib button
// drops aria/title props, so they land via the ref.
function CollapseTabButton({
  label,
  expanded,
  onClick,
}: {
  label: string;
  expanded: boolean;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const btn = ref.current;
    if (!btn) return;
    btn.setAttribute("aria-label", label);
    btn.setAttribute("title", label);
    btn.setAttribute("aria-expanded", String(expanded));
    btn.setAttribute("type", "button");
  }, [label, expanded]);
  return (
    <CollapseTab $flipped={!expanded}>
      <ButtonSmallSubtleIcon
        ref={ref}
        icon={Icon.CollapseLeft}
        onClick={onClick}
      />
    </CollapseTab>
  );
}

function TimeframeSection({
  timeframe,
  onTimeframeChange,
}: {
  timeframe: Timeframe;
  onTimeframeChange: (t: Timeframe) => void;
}) {
  return (
    <Section>
      <FieldLabel>Filter by date</FieldLabel>
      <TimeframeBoundField
        placeholder="Start date"
        testId="audit-timeframe-start"
        value={timeframe.since}
        onChange={(d) => onTimeframeChange({ ...timeframe, since: d })}
      />
      <TimeframeBoundField
        placeholder="End date"
        testId="audit-timeframe-end"
        value={timeframe.until}
        onChange={(d) => onTimeframeChange({ ...timeframe, until: d })}
      />
    </Section>
  );
}

// The resource and actor filters share one anatomy (Figma 13759-112332
// and 15642-160016): a form label above the M secondary dropdown trigger
// that names the current pick, "All …" when nothing is narrowed.
// The trigger sits inside the searchable dropdown's own context provider,
// the only place its open flag is readable.
function SingleSelectTrigger({
  testId,
  current,
  pin,
  isAll,
}: {
  testId: string;
  current: string;
  pin: PinnedOption | undefined;
  isAll: boolean;
}) {
  const { open } = useContext(GenericDropdownWithSearchContext);
  return (
    <DropdownTrigger
      type="button"
      $active={open}
      data-testid={testId}
      title={isAll || pin ? undefined : current}
    >
      {pin ? (
        pin.render
      ) : (
        <DropdownTriggerLabel>{current}</DropdownTriggerLabel>
      )}
      <IconSVG
        icon={Icon.BaseChevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
    </DropdownTrigger>
  );
}

function SingleSelectSection({
  label,
  allLabel,
  searchPlaceholder,
  noMatchLabel,
  testId,
  value,
  onChange,
  pinned = [],
  options,
  optionLabel = (v) => v,
}: {
  label: string;
  allLabel: string;
  /** The search box above the rows ("Search resources"). */
  searchPlaceholder: string;
  /** The placeholder row when the search matches nothing. */
  noMatchLabel: string;
  testId: string;
  value: string;
  onChange: (v: string) => void;
  pinned?: PinnedOption[];
  options: string[];
  optionLabel?: (v: string) => string;
}) {
  const pin = pinned.find((p) => p.value === value);
  const current =
    value === "all" ? allLabel : (pin?.label ?? optionLabel(value));
  return (
    <Section>
      <FieldLabel>{label}</FieldLabel>
      {/* Searchable: the fetched window can hold many distinct values —
          typing filters the rows (the library's built-in search box). */}
      <GenericDropdownWithSearch
        dropdownItems={buildSingleSelectItems({
          allLabel,
          pinned,
          options,
          optionLabel,
          selected: value,
          onChange,
        })}
        inputAsTrigger={false}
        inputPlaceholder={searchPlaceholder}
        noItemsLabel={noMatchLabel}
        elWidth={{ strategy: "fixed", size: 242 }}
        placement="bottom-start"
      >
        <SingleSelectTrigger
          testId={testId}
          current={current}
          pin={pin}
          isAll={value === "all"}
        />
      </GenericDropdownWithSearch>
    </Section>
  );
}

// The action and status trees share one anatomy: a parent checkbox above
// the indented child rows (Figma 13759-112336); only the rows differ.
function CheckboxTreeSection({
  label,
  testId,
  selectedCount,
  totalCount,
  onToggleAll,
  children,
}: {
  label: string;
  testId: string;
  selectedCount: number;
  totalCount: number;
  onToggleAll: (on: boolean) => void;
  children: ReactNode;
}) {
  const allSelected = selectedCount === totalCount;
  const someSelected = selectedCount > 0;
  return (
    <Section>
      {/* The lib checkbox draws the mixed dash only while checked, so
          the parent reads "any selected" with intermediate marking the
          partial state (Figma Mixed variant 13759-112367). */}
      <GenericCheckBox
        label={label}
        value={someSelected}
        intermediate={someSelected && !allSelected}
        onChange={() => onToggleAll(!allSelected)}
        testId={testId}
      />
      <ChildRows>{children}</ChildRows>
    </Section>
  );
}

function ActionRows({
  selectedActions,
  toggleAction,
}: {
  selectedActions: ReadonlySet<AuditActionGroup>;
  toggleAction: (group: AuditActionGroup) => void;
}) {
  return (
    <>
      {AUDIT_ACTION_GROUPS.map((group) => (
        <ChildRow key={group}>
          <GenericCheckBox
            value={selectedActions.has(group)}
            onChange={() => toggleAction(group)}
            testId={`audit-action-${group}`}
          />
          <GenericBadge type={BadgeType.Secondary} mode={BadgeMode.Subtle}>
            {group}
          </GenericBadge>
        </ChildRow>
      ))}
    </>
  );
}

function StatusRows({
  selectedStatuses,
  toggleStatus,
}: {
  selectedStatuses: ReadonlySet<number>;
  toggleStatus: (statusClass: number) => void;
}) {
  const theme = useTheme();
  return (
    <>
      {STATUS_CLASS_ROWS.map((row) => (
        <ChildRow key={row.statusClass}>
          <GenericCheckBox
            value={selectedStatuses.has(row.statusClass)}
            onChange={() => toggleStatus(row.statusClass)}
            testId={`audit-status-${row.statusClass}xx`}
          />
          {/* The icon rides as a child, not the badge's icon prop —
              the prop renders 16px, the design badge carries 12px. */}
          <GenericBadge type={row.type} mode={BadgeMode.Subtle}>
            <IconSVG
              icon={row.icon}
              size={12}
              colors={{ default: badgeIconColor(theme, row.type) }}
            />
            {row.label}
          </GenericBadge>
        </ChildRow>
      ))}
    </>
  );
}

// The whole rail is the expand target with hover feedback; keyboard
// access rides the inner button, and both fire the same idempotent
// expand, so a click bubbling through the button can't double-toggle.
function CollapsedSidebar({ onExpand }: { onExpand: () => void }) {
  return (
    <CollapsedRail
      data-testid="audit-filter-collapsed"
      title="Expand filters"
      onClick={onExpand}
    >
      <CollapsedLabel>Filters</CollapsedLabel>
      <CollapseTabButton
        label="Expand filters"
        expanded={false}
        onClick={onExpand}
      />
    </CollapsedRail>
  );
}

export interface AuditLogFilterSidebarProps {
  timeframe: Timeframe;
  onTimeframeChange: (t: Timeframe) => void;
  resourceFilter: string;
  onResourceChange: (v: string) => void;
  allResources: string[];
  actorFilter: string;
  onActorChange: (v: string) => void;
  allActors: string[];
  hasAgentEntries: boolean;
  selectedActions: ReadonlySet<AuditActionGroup>;
  toggleAction: (group: AuditActionGroup) => void;
  setAllActions: (on: boolean) => void;
  selectedStatuses: ReadonlySet<number>;
  toggleStatus: (statusClass: number) => void;
  setAllStatuses: (on: boolean) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}

export function AuditLogFilterSidebar({
  timeframe,
  onTimeframeChange,
  resourceFilter,
  onResourceChange,
  allResources,
  actorFilter,
  onActorChange,
  allActors,
  hasAgentEntries,
  selectedActions,
  toggleAction,
  setAllActions,
  selectedStatuses,
  toggleStatus,
  setAllStatuses,
  collapsed,
  setCollapsed,
}: AuditLogFilterSidebarProps) {
  if (collapsed) {
    return <CollapsedSidebar onExpand={() => setCollapsed(false)} />;
  }

  return (
    <Sidebar data-testid="audit-filter-sidebar">
      {/* Every section scrolls as one on a short screen while the card
          keeps its height (TTTL-186); the collapse tab stays put below. */}
      <SidebarScroll>
        <TimeframeSection
          timeframe={timeframe}
          onTimeframeChange={onTimeframeChange}
        />
        <Divider />
        <SingleSelectSection
          label="Filter by resource"
          allLabel="All resources"
          searchPlaceholder="Search resources"
          noMatchLabel="No resources found"
          testId="audit-resource-filter"
          value={resourceFilter}
          onChange={onResourceChange}
          options={allResources}
          optionLabel={resourceLabel}
        />
        <Divider />
        <SingleSelectSection
          label="Filter by actor"
          allLabel="All actors"
          searchPlaceholder="Search actors"
          noMatchLabel="No actors found"
          testId="audit-actor-filter"
          value={actorFilter}
          onChange={onActorChange}
          // The agent pick sits second, right under "All actors", and only
          // while the fetched window holds agent entries — a remembered pick
          // still renders so the user can see why the table is empty.
          pinned={
            hasAgentEntries || actorFilter === AGENT_ACTOR_FILTER
              ? [
                  {
                    value: AGENT_ACTOR_FILTER,
                    label: "agent",
                    render: <AgentBadge />,
                  },
                ]
              : []
          }
          options={allActors}
        />
        <Divider />
        <CheckboxTreeSection
          label="All actions"
          testId="audit-all-actions"
          selectedCount={selectedActions.size}
          totalCount={AUDIT_ACTION_GROUPS.length}
          onToggleAll={setAllActions}
        >
          <ActionRows
            selectedActions={selectedActions}
            toggleAction={toggleAction}
          />
        </CheckboxTreeSection>
        <Divider />
        <CheckboxTreeSection
          label="All status"
          testId="audit-all-status"
          selectedCount={selectedStatuses.size}
          totalCount={ALL_STATUS_CLASSES.length}
          onToggleAll={setAllStatuses}
        >
          <StatusRows
            selectedStatuses={selectedStatuses}
            toggleStatus={toggleStatus}
          />
        </CheckboxTreeSection>
      </SidebarScroll>
      <CollapseTabButton
        label="Collapse filters"
        expanded
        onClick={() => setCollapsed(true)}
      />
    </Sidebar>
  );
}

const Sidebar = styled.div`
  position: relative;
  width: ${SIDEBAR_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.cards1Background};
  border-right: 1px solid ${(p) => p.theme.cards1Border};
  /* No header bar above this card (unlike /logs) — the sidebar carries
     both left corners of the card, which doesn't overflow-clip (the
     collapse tab overlaps its borders). */
  border-radius: 8px 0 0 8px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACINGS}px;
  padding: ${SPACINGL}px ${SPACINGM}px;
  width: 100%;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  background: ${(p) => p.theme.cards1Border};
`;

/* The sidebar's sections, scrolling as one on the app's overlay
   scrollbars. The bottom padding lets the last rows scroll clear of the
   collapse tab that overlays the sidebar's bottom-right corner. */
const SidebarScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  padding-bottom: 44px;
`;

/* Figma Form/label: Inter Medium 14 on input/textlabel, centered in a
   20px-high box. */
const FieldLabel = styled.span`
  display: flex;
  align-items: center;
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${(p) => p.theme.inputLabel};
`;

const TimeframeRoot = styled.div`
  position: relative;
  width: 100%;
`;

/* The design's underline TextInput face (h 32, 4px side padding, the
   default input border as the bottom rule); the whole row opens the
   picker. A real button — the calendar glyph inside is a plain svg, so
   nothing nests. */
const TimeframeInput = styled.button`
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.input.gapinternal}px;
  width: 100%;
  height: 32px;
  padding: 0 ${designVariables.ui.input.paddingw}px;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.inputDefaultBorder};
  cursor: pointer;
  text-align: left;

  img,
  svg {
    flex-shrink: 0;
  }
`;

const TimeframeText = styled.span<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.2;
  color: ${(p) =>
    p.$placeholder
      ? p.theme.semanticTextPlaceholder
      : p.theme.semanticTextBody};
`;

/* Positions the calendar panel under its field, above the table — the
   lib picker view brings its own card chrome (shadow, radius, width). */
/* Positioned on screen under its field (the anchor is inline style), so
   the sidebar's scroll region never clips it; the lib picker view brings
   its own card chrome (shadow, radius, width). */
const TimeframePopover = styled.div`
  position: fixed;
  z-index: 20;
`;

/* The × that clears a set bound: an absolute SIBLING overlaying the
   field's right side (left of the calendar glyph) — inside the field
   button it would nest interactive elements. */
const FieldClearButton = styled.button`
  position: absolute;
  /* A 20px hover pill centered in the field's 32px row. */
  top: 6px;
  right: ${designVariables.ui.input.paddingw + 16 + designVariables.ui.input.gapinternal}px;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${designVariables.ui.button.corner}px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.buttonSubtleHoverBackground};
  }
`;

/* The "All resources" / "All actors" dropdown trigger — the design's M
   secondary button with the small 10px chevron (Figma 13759-112332,
   116x32); the prebuilt lib buttons render every icon at 16px, which
   oversizes the chevron. */
const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

const DropdownTrigger = styled.button<{ $active?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  max-width: 100%;

  img,
  svg {
    flex-shrink: 0;
  }
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => p.theme.buttonSecondaryDefaultText};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonSecondaryActiveBackground
      : p.theme.buttonSecondaryDefaultBackground};
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme.buttonSecondaryActiveBorder
        : p.theme.buttonSecondaryDefaultBorder};
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
    border-color: ${(p) => p.theme.buttonSecondaryHoverBorder};
  }
`;

const DropdownTriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* The children hang off a borderlight rule, indented one spacing on each
   side of it (Figma 13759-112336). The inner spacing is 10px minus the
   1px rule — Figma strokes draw inside the padding, CSS borders add to
   it. */
const ChildRows = styled.div`
  margin-left: ${SPACINGS}px;
  padding-left: ${SPACINGS - 1}px;
  border-left: 1px solid ${(p) => p.theme.semanticBorderLight};
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS}px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.s}px;
`;

/* The collapse tab hugging the sidebar's bottom-right edge — the /logs
   sidebar's tab, pixel for pixel (Figma 13759-112385). */
const CollapseTab = styled.div<{ $flipped?: boolean }>`
  position: absolute;
  right: 0;
  bottom: -1px;
  width: ${TAB_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${designVariables.primitives.spacings.m - 1}px 0;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.semanticBorderDark};
  border-right: none;
  border-bottom-color: ${(p) => p.theme.cards1Border};
  border-radius: ${designVariables.primitives.corner.xl}px 0 0
    ${designVariables.primitives.corner.xl}px;

  svg,
  img {
    transform: rotate(${(p) => (p.$flipped ? 180 : 0)}deg);
  }
`;

/* Collapsed, the rail keeps the tab's own width and the tab loses its
   borders entirely; the whole rail hovers/clicks as one expand target. */
const CollapsedRail = styled.div`
  position: relative;
  width: ${TAB_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: ${designVariables.primitives.spacings.m}px;
  background: ${(p) => p.theme.cards1Background};
  border-right: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 8px 0 0 8px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }

  ${CollapseTab} {
    position: static;
    width: 100%;
    border: none;
    background: transparent;
  }
`;

const CollapsedLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;
